import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    fetchPosLocations,
    addPosLocation,
    deletePosLocation,
    fetchPosReaders,
    addPosReader,
    removePosReader,
    fetchAllTargets,
    fetchS700Targets,
    saveS700Targets,
    clearPosAdminError,
    fetchReaderStatus,
    startDonationFlow,
    cancelReaderAction
} from '../redux/actions';
import LogoSection from '../components/LogoSection';

const POSAdmin = () => {
    const dispatch = useDispatch();
    const tenantId = useSelector((state) => state.userTenant);
    const userRole = useSelector((state) => state.userRole);
    const posAdmin = useSelector((state) => state.posAdmin);

    // Section collapse states
    const [locationsOpen, setLocationsOpen] = useState(true);
    const [readersOpen, setReadersOpen] = useState(false);
    const [targetsOpen, setTargetsOpen] = useState(false);

    // Add location form state
    const [showAddLocationForm, setShowAddLocationForm] = useState(false);
    const [newLocation, setNewLocation] = useState({
        displayName: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'CA'
    });

    // Add reader form state
    const [showAddReaderForm, setShowAddReaderForm] = useState(false);
    const [newReader, setNewReader] = useState({
        registrationCode: '',
        label: '',
        deviceType: 'stripe_s700'
    });

    // Target selection modal state
    const [showTargetModal, setShowTargetModal] = useState(false);
    const [selectedTargets, setSelectedTargets] = useState([]);

    // Delete confirmation dialog state
    const [deleteDialog, setDeleteDialog] = useState({
        show: false,
        type: null, // 'location' or 'reader'
        id: null,
        name: null
    });

    // Reader status modal state
    const [statusModal, setStatusModal] = useState({
        show: false,
        loading: false,
        reader: null,
        error: null
    });

    const isAdmin = userRole === 'admin';
    const isTenantAdmin = userRole === 'tenant-admin';
    const canManagePOS = isAdmin || isTenantAdmin;

    // Load data on mount
    useEffect(() => {
        if (tenantId && canManagePOS) {
            dispatch(fetchPosLocations(tenantId));
            dispatch(fetchPosReaders(tenantId));
            dispatch(fetchAllTargets(tenantId));
            dispatch(fetchS700Targets(tenantId));
        }
    }, [dispatch, tenantId, canManagePOS]);

    // Sync selected targets when S700 targets are loaded
    useEffect(() => {
        if (posAdmin.s700Targets.length > 0) {
            setSelectedTargets(posAdmin.s700Targets.map(t => t.id || t.targetID));
        }
    }, [posAdmin.s700Targets]);

    // Handle add location
    const handleAddLocation = async () => {
        if (!newLocation.displayName.trim()) {
            alert('Display name is required');
            return;
        }
        if (!newLocation.line1.trim() || !newLocation.city.trim() ||
            !newLocation.state.trim() || !newLocation.postalCode.trim()) {
            alert('Please fill in all required address fields');
            return;
        }

        const address = {
            line1: newLocation.line1,
            line2: newLocation.line2 || undefined,
            city: newLocation.city,
            state: newLocation.state,
            postal_code: newLocation.postalCode,
            country: newLocation.country
        };

        const result = await dispatch(addPosLocation(tenantId, newLocation.displayName, address));
        if (result.success) {
            setNewLocation({
                displayName: '',
                line1: '',
                line2: '',
                city: '',
                state: '',
                postalCode: '',
                country: 'CA'
            });
            setShowAddLocationForm(false);
        } else {
            alert(result.error || 'Failed to add location');
        }
    };

    // Handle delete location
    const handleDeleteLocation = async () => {
        const result = await dispatch(deletePosLocation(tenantId, deleteDialog.id));
        if (result.success) {
            setDeleteDialog({ show: false, type: null, id: null, name: null });
        } else {
            alert(result.error || 'Failed to delete location');
        }
    };

    // Handle add reader
    const handleAddReader = async () => {
        if (!newReader.registrationCode.trim()) {
            alert('Registration code is required');
            return;
        }
        if (!newReader.label.trim()) {
            alert('Label is required');
            return;
        }

        if (posAdmin.locations.length === 0) {
            alert('Please add a location first before adding a reader');
            return;
        }

        const result = await dispatch(addPosReader(
            tenantId,
            newReader.registrationCode,
            newReader.label,
            newReader.deviceType
        ));
        if (result.success) {
            setNewReader({
                registrationCode: '',
                label: '',
                deviceType: 'stripe_s700'
            });
            setShowAddReaderForm(false);
        } else {
            alert(result.error || 'Failed to add reader');
        }
    };

    // Handle delete reader
    const handleDeleteReader = async () => {
        const result = await dispatch(removePosReader(tenantId, deleteDialog.id));
        if (result.success) {
            setDeleteDialog({ show: false, type: null, id: null, name: null });
        } else {
            alert(result.error || 'Failed to remove reader');
        }
    };

    // Handle get reader status
    const handleGetReaderStatus = async (readerId) => {
        setStatusModal({ show: true, loading: true, reader: null, error: null });
        const result = await dispatch(fetchReaderStatus(tenantId, readerId));
        if (result.success) {
            setStatusModal({ show: true, loading: false, reader: result.reader, error: null });
        } else {
            setStatusModal({ show: true, loading: false, reader: null, error: result.error });
        }
    };

    // Handle cancel/stop reader action
    const handleCancelReader = async (readerId) => {
        const result = await dispatch(cancelReaderAction(tenantId, readerId));
        if (result.success) {
            alert('Reader action cancelled successfully');
        } else {
            alert(result.error || 'Failed to cancel reader action');
        }
    };

    // Handle start donation flow
    const handleStartDonationFlow = async (readerId) => {
        const result = await dispatch(startDonationFlow(tenantId, readerId));
        if (result.success) {
            alert('Donation flow started on reader');
        } else {
            alert(result.error || 'Failed to start donation flow');
        }
    };

    // Handle target selection toggle
    const handleTargetToggle = (targetId) => {
        setSelectedTargets(prev => {
            if (prev.includes(targetId)) {
                return prev.filter(id => id !== targetId);
            } else if (prev.length < 4) {
                return [...prev, targetId];
            } else {
                alert('Maximum 4 targets allowed');
                return prev;
            }
        });
    };

    // Handle save targets
    const handleSaveTargets = async () => {
        // Build targets array with full data
        const targetsToSave = selectedTargets.map((targetId, index) => {
            const target = posAdmin.allTargets.find(t => t.id === targetId);
            return {
                id: target.id,
                name: target.name,
                type: target.cause ? 'cause' : 'campaign',
                orgID: target.orgID,
                sort_order: index + 1
            };
        });

        // Validate all targets have orgID before saving
        const missingOrgID = targetsToSave.find(t => !t.orgID);
        if (missingOrgID) {
            alert(`Target "${missingOrgID.name}" is missing orgID. Please ensure the tenant has an org_id configured.`);
            return;
        }

        const result = await dispatch(saveS700Targets(tenantId, targetsToSave));
        if (result.success) {
            setShowTargetModal(false);
        } else {
            alert(result.error || 'Failed to save targets');
        }
    };

    // Move target up in order
    const moveTargetUp = (index) => {
        if (index === 0) return;
        const newOrder = [...selectedTargets];
        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
        setSelectedTargets(newOrder);
    };

    // Move target down in order
    const moveTargetDown = (index) => {
        if (index === selectedTargets.length - 1) return;
        const newOrder = [...selectedTargets];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        setSelectedTargets(newOrder);
    };

    // Remove target from selection
    const removeTarget = (targetId) => {
        setSelectedTargets(prev => prev.filter(id => id !== targetId));
    };

    // Get target name by ID
    const getTargetName = (targetId) => {
        const target = posAdmin.allTargets.find(t => t.id === targetId);
        return target ? target.name : targetId;
    };

    // Get target by ID
    const getTarget = (targetId) => {
        return posAdmin.allTargets.find(t => t.id === targetId);
    };

    // Get location name by ID
    const getLocationName = (locationId) => {
        const location = posAdmin.locations.find(l => l.id === locationId);
        return location ? location.display_name : locationId || 'N/A';
    };

    if (!canManagePOS) {
        return (
            <div className="min-h-screen flex justify-center items-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
                    <p className="mt-2 text-gray-600">You do not have permission to access this page.</p>
                    <Link to="/" className="mt-4 inline-block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                        Return Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <LogoSection />

                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">POS Administration</h1>
                    <p className="text-gray-600">Manage locations, readers, and donation targets for your POS terminals</p>
                </div>

                {/* Error Display */}
                {posAdmin.error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
                        <p className="text-red-600">{posAdmin.error}</p>
                        <button
                            onClick={() => dispatch(clearPosAdminError())}
                            className="text-red-600 hover:text-red-800"
                        >
                            Dismiss
                        </button>
                    </div>
                )}

                {/* Locations Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                    <button
                        onClick={() => setLocationsOpen(!locationsOpen)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                        <h2 className="text-lg font-semibold text-gray-900">
                            Locations ({posAdmin.locations.length})
                        </h2>
                        <svg
                            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${locationsOpen ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {locationsOpen && (
                        <div className="px-6 pb-6 border-t border-gray-200 pt-4">
                            {posAdmin.locationsLoading ? (
                                <p className="text-gray-500 text-center py-4">Loading locations...</p>
                            ) : (
                                <>
                                    {posAdmin.locations.length === 0 ? (
                                        <p className="text-gray-500 text-center py-4">No locations configured. Add a location to get started.</p>
                                    ) : (
                                        <div className="overflow-x-auto mb-4">
                                            <table className="min-w-full">
                                                <thead>
                                                    <tr className="bg-gray-50">
                                                        <th className="py-2 px-4 text-left text-sm font-medium text-gray-700">Name</th>
                                                        <th className="py-2 px-4 text-left text-sm font-medium text-gray-700">Address</th>
                                                        <th className="py-2 px-4 text-center text-sm font-medium text-gray-700">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {posAdmin.locations.map((location) => (
                                                        <tr key={location.id} className="border-t border-gray-200">
                                                            <td className="py-3 px-4 text-sm text-gray-900">{location.display_name}</td>
                                                            <td className="py-3 px-4 text-sm text-gray-600">
                                                                {location.address ?
                                                                    `${location.address.line1}, ${location.address.city}, ${location.address.state}` :
                                                                    'N/A'}
                                                            </td>
                                                            <td className="py-3 px-4 text-center">
                                                                <button
                                                                    onClick={() => setDeleteDialog({
                                                                        show: true,
                                                                        type: 'location',
                                                                        id: location.id,
                                                                        name: location.display_name
                                                                    })}
                                                                    className="text-red-600 hover:text-red-800"
                                                                    title="Delete location"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                    </svg>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {!showAddLocationForm ? (
                                        <button
                                            onClick={() => setShowAddLocationForm(true)}
                                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                        >
                                            + Add Location
                                        </button>
                                    ) : (
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <h3 className="font-medium text-gray-900 mb-3">Add New Location</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                                                    <input
                                                        type="text"
                                                        value={newLocation.displayName}
                                                        onChange={(e) => setNewLocation({ ...newLocation, displayName: e.target.value })}
                                                        placeholder="e.g., Main Office"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
                                                    <input
                                                        type="text"
                                                        value={newLocation.line1}
                                                        onChange={(e) => setNewLocation({ ...newLocation, line1: e.target.value })}
                                                        placeholder="123 Main Street"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                                                    <input
                                                        type="text"
                                                        value={newLocation.line2}
                                                        onChange={(e) => setNewLocation({ ...newLocation, line2: e.target.value })}
                                                        placeholder="Suite 100 (optional)"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                                    <input
                                                        type="text"
                                                        value={newLocation.city}
                                                        onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                                                        placeholder="Toronto"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">State/Province *</label>
                                                    <input
                                                        type="text"
                                                        value={newLocation.state}
                                                        onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
                                                        placeholder="ON"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
                                                    <input
                                                        type="text"
                                                        value={newLocation.postalCode}
                                                        onChange={(e) => setNewLocation({ ...newLocation, postalCode: e.target.value })}
                                                        placeholder="M5V 1A1"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                                                    <select
                                                        value={newLocation.country}
                                                        onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="CA">Canada</option>
                                                        <option value="US">United States</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button
                                                    onClick={handleAddLocation}
                                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                >
                                                    Add Location
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowAddLocationForm(false);
                                                        setNewLocation({
                                                            displayName: '',
                                                            line1: '',
                                                            line2: '',
                                                            city: '',
                                                            state: '',
                                                            postalCode: '',
                                                            country: 'CA'
                                                        });
                                                    }}
                                                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Readers Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                    <button
                        onClick={() => setReadersOpen(!readersOpen)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                        <h2 className="text-lg font-semibold text-gray-900">
                            Readers ({posAdmin.readers.length})
                        </h2>
                        <svg
                            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${readersOpen ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {readersOpen && (
                        <div className="px-6 pb-6 border-t border-gray-200 pt-4">
                            {posAdmin.readersLoading ? (
                                <p className="text-gray-500 text-center py-4">Loading readers...</p>
                            ) : (
                                <>
                                    {posAdmin.readers.length === 0 ? (
                                        <p className="text-gray-500 text-center py-4">No readers configured. Add a reader to get started.</p>
                                    ) : (
                                        <div className="overflow-x-auto mb-4">
                                            <table className="min-w-full">
                                                <thead>
                                                    <tr className="bg-gray-50">
                                                        <th className="py-2 px-4 text-left text-sm font-medium text-gray-700">Label</th>
                                                        <th className="py-2 px-4 text-left text-sm font-medium text-gray-700">Terminal ID</th>
                                                        <th className="py-2 px-4 text-left text-sm font-medium text-gray-700">Device Type</th>
                                                        <th className="py-2 px-4 text-left text-sm font-medium text-gray-700">Status</th>
                                                        <th className="py-2 px-4 text-left text-sm font-medium text-gray-700">Location</th>
                                                        <th className="py-2 px-4 text-center text-sm font-medium text-gray-700">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {posAdmin.readers.map((reader) => (
                                                        <tr key={reader.id} className="border-t border-gray-200">
                                                            <td className="py-3 px-4 text-sm text-gray-900">{reader.label || 'N/A'}</td>
                                                            <td className="py-3 px-4 text-sm text-gray-600 font-mono text-xs">{reader.id}</td>
                                                            <td className="py-3 px-4 text-sm text-gray-600">
                                                                {reader.device_type === 'stripe_s700' ? 'S700' :
                                                                    reader.device_type === 'bbpos_wisepos_e' ? 'WisePOS E' :
                                                                        reader.device_type}
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${reader.status === 'online'
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {reader.status || 'offline'}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-gray-600">{getLocationName(reader.location)}</td>
                                                            <td className="py-3 px-4 text-center">
                                                                <div className="flex items-center justify-center space-x-2">
                                                                    <button
                                                                        onClick={() => handleStartDonationFlow(reader.id)}
                                                                        className="text-green-600 hover:text-green-800"
                                                                        title="Start donation flow"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleGetReaderStatus(reader.id)}
                                                                        className="text-blue-600 hover:text-blue-800"
                                                                        title="Get reader status"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleCancelReader(reader.id)}
                                                                        className="text-orange-600 hover:text-orange-800"
                                                                        title="Cancel/stop reader action"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setDeleteDialog({
                                                                            show: true,
                                                                            type: 'reader',
                                                                            id: reader.id,
                                                                            name: reader.label || reader.id
                                                                        })}
                                                                        className="text-red-600 hover:text-red-800"
                                                                        title="Remove reader"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {!showAddReaderForm ? (
                                        <button
                                            onClick={() => {
                                                if (posAdmin.locations.length === 0) {
                                                    alert('Please add a location first before adding a reader');
                                                    return;
                                                }
                                                setShowAddReaderForm(true);
                                            }}
                                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                        >
                                            + Add Reader
                                        </button>
                                    ) : (
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <h3 className="font-medium text-gray-900 mb-3">Add New Reader</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Code *</label>
                                                    <input
                                                        type="text"
                                                        value={newReader.registrationCode}
                                                        onChange={(e) => setNewReader({ ...newReader, registrationCode: e.target.value })}
                                                        placeholder="Enter code from Stripe Terminal"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
                                                    <input
                                                        type="text"
                                                        value={newReader.label}
                                                        onChange={(e) => setNewReader({ ...newReader, label: e.target.value })}
                                                        placeholder="e.g., Front Desk Terminal"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Device Type *</label>
                                                    <select
                                                        value={newReader.deviceType}
                                                        onChange={(e) => setNewReader({ ...newReader, deviceType: e.target.value })}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="stripe_s700">S700</option>
                                                        <option value="bbpos_wisepos_e">WisePOS E</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button
                                                    onClick={handleAddReader}
                                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                >
                                                    Add Reader
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowAddReaderForm(false);
                                                        setNewReader({
                                                            registrationCode: '',
                                                            label: '',
                                                            deviceType: 'stripe_s700'
                                                        });
                                                    }}
                                                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Donation Targets Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                    <button
                        onClick={() => setTargetsOpen(!targetsOpen)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                        <h2 className="text-lg font-semibold text-gray-900">
                            Donation Targets ({posAdmin.s700Targets.length}/4)
                        </h2>
                        <svg
                            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${targetsOpen ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {targetsOpen && (
                        <div className="px-6 pb-6 border-t border-gray-200 pt-4">
                            {posAdmin.s700TargetsLoading ? (
                                <p className="text-gray-500 text-center py-4">Loading targets...</p>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Select up to 4 donation targets that will appear on S700 terminals. Drag to reorder.
                                    </p>

                                    {selectedTargets.length === 0 ? (
                                        <p className="text-gray-500 text-center py-4">No targets selected. Configure targets to display on S700 terminals.</p>
                                    ) : (
                                        <div className="space-y-2 mb-4">
                                            {selectedTargets.map((targetId, index) => {
                                                const target = getTarget(targetId);
                                                return (
                                                    <div
                                                        key={targetId}
                                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                                    >
                                                        <div className="flex items-center space-x-3">
                                                            <span className="text-gray-400 font-medium w-6">{index + 1}.</span>
                                                            <span className="font-medium text-gray-900">{target?.name || targetId}</span>
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${target?.cause ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                                                }`}>
                                                                {target?.cause ? 'Cause' : 'Campaign'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <button
                                                                onClick={() => moveTargetUp(index)}
                                                                disabled={index === 0}
                                                                className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}
                                                                title="Move up"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => moveTargetDown(index)}
                                                                disabled={index === selectedTargets.length - 1}
                                                                className={`p-1 rounded ${index === selectedTargets.length - 1 ? 'text-gray-300' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}
                                                                title="Move down"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => removeTarget(targetId)}
                                                                className="p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                title="Remove target"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setShowTargetModal(true)}
                                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            Configure Targets
                                        </button>
                                        {selectedTargets.length > 0 && (
                                            <button
                                                onClick={handleSaveTargets}
                                                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                            >
                                                Save Changes
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Refresh Data Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Refresh Data</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Manually refresh data from the S700 API endpoints.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                            onClick={() => dispatch(fetchPosLocations(tenantId))}
                            disabled={posAdmin.locationsLoading}
                            className={`px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                                posAdmin.locationsLoading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-100 text-blue-900 hover:bg-blue-200'
                            }`}
                        >
                            {posAdmin.locationsLoading ? 'Loading...' : 'Fetch Locations'}
                        </button>
                        <button
                            onClick={() => dispatch(fetchPosReaders(tenantId))}
                            disabled={posAdmin.readersLoading}
                            className={`px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                                posAdmin.readersLoading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-green-100 text-green-900 hover:bg-green-200'
                            }`}
                        >
                            {posAdmin.readersLoading ? 'Loading...' : 'Fetch Readers'}
                        </button>
                        <button
                            onClick={() => dispatch(fetchS700Targets(tenantId))}
                            disabled={posAdmin.s700TargetsLoading}
                            className={`px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                                posAdmin.s700TargetsLoading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-purple-100 text-purple-900 hover:bg-purple-200'
                            }`}
                        >
                            {posAdmin.s700TargetsLoading ? 'Loading...' : 'Fetch Targets'}
                        </button>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                        <p>Locations: {posAdmin.locations.length} | Readers: {posAdmin.readers.length} | Targets: {posAdmin.s700Targets.length}/4</p>
                    </div>
                </div>

                {/* Back to Settings Link */}
                <div className="text-center">
                    <Link
                        to="/settings"
                        className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Back to Settings
                    </Link>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            {deleteDialog.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                        <p className="mb-4">
                            Are you sure you want to delete {deleteDialog.type} "{deleteDialog.name}"?
                            {deleteDialog.type === 'location' && (
                                <span className="block mt-2 text-sm text-red-600">
                                    Note: You cannot delete a location if readers are attached to it.
                                </span>
                            )}
                        </p>
                        <div className="flex space-x-4 justify-end">
                            <button
                                onClick={() => setDeleteDialog({ show: false, type: null, id: null, name: null })}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={deleteDialog.type === 'location' ? handleDeleteLocation : handleDeleteReader}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Target Selection Modal */}
            {showTargetModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Select Donation Targets (max 4)</h2>
                            <button
                                onClick={() => setShowTargetModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            Selected: {selectedTargets.length}/4
                        </p>

                        {posAdmin.allTargetsLoading ? (
                            <p className="text-gray-500 text-center py-4">Loading targets...</p>
                        ) : posAdmin.allTargets.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">
                                No targets available. Please sync targets from the main Infaque app.
                            </p>
                        ) : (
                            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
                                {posAdmin.allTargets.map((target) => (
                                    <label
                                        key={target.id}
                                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${selectedTargets.includes(target.id)
                                                ? 'bg-blue-50 border-blue-300'
                                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedTargets.includes(target.id)}
                                            onChange={() => handleTargetToggle(target.id)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-3 flex-1">
                                            <span className="font-medium text-gray-900">{target.name}</span>
                                            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${target.cause ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                                }`}>
                                                {target.cause ? 'Cause' : 'Campaign'}
                                            </span>
                                        </span>
                                        {selectedTargets.includes(target.id) && (
                                            <span className="text-sm text-blue-600 font-medium">
                                                #{selectedTargets.indexOf(target.id) + 1}
                                            </span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        )}

                        <div className="flex space-x-4 justify-end pt-4 border-t border-gray-200">
                            <button
                                onClick={() => setShowTargetModal(false)}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowTargetModal(false);
                                }}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reader Status Modal */}
            {statusModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Reader Status</h2>
                            <button
                                onClick={() => setStatusModal({ show: false, loading: false, reader: null, error: null })}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {statusModal.loading ? (
                            <div className="flex-1 flex items-center justify-center py-8">
                                <p className="text-gray-500">Loading reader status...</p>
                            </div>
                        ) : statusModal.error ? (
                            <div className="flex-1 py-4">
                                <p className="text-red-600">Error: {statusModal.error}</p>
                            </div>
                        ) : statusModal.reader ? (
                            <div className="flex-1 overflow-auto">
                                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto whitespace-pre-wrap">
                                    {JSON.stringify(statusModal.reader, null, 2)}
                                </pre>
                            </div>
                        ) : null}

                        <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
                            <button
                                onClick={() => setStatusModal({ show: false, loading: false, reader: null, error: null })}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default POSAdmin;
